import { useEffect, useId, useRef, useState } from 'react';

/**
 * Menu suspenso acessível: abre com clique/Enter, fecha com Esc ou clique fora,
 * setas navegam entre os itens. `items`: [{ label, icon, onClick, danger, separator, heading }]
 */
export function Menu({ trigger, items, align = 'right', label }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrap.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    wrap.current?.querySelector('.menu-item')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      wrap.current?.querySelector('[aria-haspopup]')?.focus();
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const els = [...wrap.current.querySelectorAll('.menu-item')];
      const i = els.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % els.length : (i - 1 + els.length) % els.length;
      els[next]?.focus();
    }
  };

  return (
    <div ref={wrap} style={{ position: 'relative', display: 'inline-flex' }} onKeyDown={onKeyDown}>
      {trigger({
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': menuId,
        onClick: () => setOpen((o) => !o),
      })}
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          className="menu"
          style={{ top: 'calc(100% + 6px)', [align]: 0 }}
        >
          {items.filter(Boolean).map((it, i) =>
            it.separator ? (
              <div key={i} className="menu-sep" role="separator" />
            ) : it.heading ? (
              <div key={i} className="menu-label">
                {it.heading}
              </div>
            ) : (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={`menu-item${it.danger ? ' danger' : ''}`}
                aria-current={it.current || undefined}
                onClick={() => {
                  setOpen(false);
                  it.onClick?.();
                }}
              >
                {it.icon && <it.icon aria-hidden="true" />}
                <span className="truncate" style={{ flex: 1 }}>
                  {it.label}
                </span>
                {it.right}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
